import type {ContextProvider, ContextProviderDataGeneric} from '@bitstillery/common/lib/context'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, FieldSwitch, Icon} from '@bitstillery/common/components'
import {classes, present, unique_id} from '@bitstillery/common/lib/utils'
import {is_filter_activated, clear_filter, generate_filters, reset_filters_sort} from '@bitstillery/common/lib/filters'
import {$t} from '@bitstillery/common/app'
import {watch, type Unwatch} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import type {WorkflowDefinition} from '@bitstillery/common/api/fact2server'
import type {CollectionProxy} from '@bitstillery/common/lib/collection'
import type {FilterSelectSingleState} from '@bitstillery/common/types'

interface FilterWorkflowAttrs {
    className?: string
    collection: CollectionProxy
    context?: ContextProvider<ContextProviderDataGeneric> | undefined
    /** The meta's workflow filter; its `context` holds the workflows. */
    filter: FilterSelectSingleState
    disabled?: boolean
}

export class FilterWorkflow extends MithrilComponent<FilterWorkflowAttrs> {
    watchers: Unwatch[] = []
    name = unique_id()
    _include_snoozed_watcher_added = false

    override oncreate(vnode: m.Vnode<FilterWorkflowAttrs>) {
        // Watch filter.selection to disable/enable related filters when workflow changes
        this.watchers.push(
            watch(present(vnode.attrs.filter.$selection, 'workflow selection signal'), () => {
                vnode.attrs.context?.select_none(vnode.attrs.collection)
                this.update_filter_state(vnode, true)
            }),
        )
        // Initial update
        this.update_filter_state(vnode, false)
    }

    override onupdate(vnode: m.Vnode<FilterWorkflowAttrs>) {
        // Watch include_snoozed filter to refresh meta when toggled (workflow counts depend on it)
        const include_snoozed_filter = vnode.attrs.collection.filters.include_snoozed
        if (include_snoozed_filter && !this._include_snoozed_watcher_added) {
            this._include_snoozed_watcher_added = true
            this.watchers.push(
                watch(present(include_snoozed_filter.$selection, 'include_snoozed selection signal'), () => {
                    void vnode.attrs.collection.refresh_meta()
                }),
            )
        }
    }

    override onremove() {
        this.watchers.forEach((unwatch) => unwatch())
    }

    /**
     * Format filter conditions for display in tooltip
     */
    private format_filter_conditions(workflow: WorkflowDefinition): m.Vnode {
        if (Object.keys(workflow.filter_labels).length === 0) {
            return <div>{$t('filters.workflow.no_filter_conditions')}</div>
        }

        const filter_items: m.Vnode[] = []

        for (const [filter_name, filter_value] of Object.entries(workflow.filter_labels)) {
            filter_items.push(
                <div className='fl mt-1' style='align-items: baseline;'>
                    <span className='fw-600 text-d mw-120' style='flex-shrink: 0;'>
                        {filter_name}
                    </span>
                    <span className='mr-1 ml-1' style='flex-shrink: 0;'>
                        :
                    </span>
                    <span className='fl-1 text-d fw-400'>{filter_value}</span>
                </div>,
            )
        }

        const filter_count = Object.keys(workflow.filter_labels).length
        return (
            <div>
                <div className='mb-1 fw-500 text-d fw-600'>
                    This workflow applies {filter_count === 1 ? 'one filter' : `${filter_count} filters`}:
                </div>
                <div style='border-top: 1px solid #e0e0e0;' className='mt-1 mb-1'></div>
                {filter_items}
            </div>
        )
    }

    private update_filter_state(vnode: m.Vnode<FilterWorkflowAttrs>, reset_snoozed: boolean = false) {
        const workflows = vnode.attrs.filter.context
        const active_workflow_id = vnode.attrs.filter.selection

        if (!workflows) {
            return
        }

        // Find the active workflow definition
        const active_workflow = active_workflow_id ? workflows.find((wf) => wf.workflow_id === active_workflow_id) : null

        // Track which filters we've processed to avoid duplicates
        const processed_filters = new Set<string>()

        // Enable/disable filters based on active workflow
        for (const workflow of workflows) {
            for (const filter_name of Object.keys(workflow.filters)) {
                if (processed_filters.has(filter_name)) continue
                processed_filters.add(filter_name)

                const filter = vnode.attrs.collection.filters[filter_name]
                if (!filter) continue

                if (active_workflow && filter_name in active_workflow.filters) {
                    // Disable filters that are controlled by the active workflow
                    filter.disabled = true
                    // Clear selection if the filter has one
                    if (is_filter_activated(filter, true)) {
                        clear_filter(filter)
                    }
                } else {
                    // Enable filters that are not controlled by the active workflow
                    filter.disabled = false
                }
            }
        }

        // When workflow selection changes, clear "include snoozed" so the default is off for the new workflow
        const include_snoozed_filter = vnode.attrs.collection.filters.include_snoozed
        if (reset_snoozed && include_snoozed_filter && is_filter_activated(include_snoozed_filter, false)) {
            clear_filter(include_snoozed_filter)
        }
    }

    private workflow_has_snooze_action(workflow_def: WorkflowDefinition): boolean {
        return workflow_def.actions.some((a) => a.label === 'Snooze')
    }

    view(vnode: m.Vnode<FilterWorkflowAttrs>) {
        if (!vnode.attrs.collection.state.ready) return null

        const workflows = vnode.attrs.filter.context
        const active_workflow = vnode.attrs.filter.selection

        const active_workflow_def = active_workflow ? workflows?.find((wf) => wf.workflow_id === active_workflow) : null

        // Ensure include_snoozed filter exists when this workflow has Snooze (e.g. if meta was cached before toggle was added)
        const show_include_snoozed = active_workflow_def && this.workflow_has_snooze_action(active_workflow_def)
        if (show_include_snoozed && !('include_snoozed' in vnode.attrs.collection.filters)) {
            vnode.attrs.collection.filters.include_snoozed = generate_filters({
                include_snoozed: {
                    default: false,
                    selection: false,
                    type: 'TOGGLE',
                    placement: {order: -1},
                    serialize: 'boolean',
                },
            }).include_snoozed
        }
        const include_snoozed_filter = vnode.attrs.collection.filters.include_snoozed

        return (
            <div
                className={classes('c-filter-workflow', 'filter', vnode.attrs.className, {
                    'is-active': !!active_workflow,
                    disabled: vnode.attrs.collection.state.bulk_mode || vnode.attrs.disabled,
                })}
            >
                <div className='filter-workflow-badge'>
                    <Button
                        className='filter-workflow-badge-icon-wrapper'
                        disabled={vnode.attrs.disabled || !workflows || workflows.length === 0}
                        icon={active_workflow ? 'pause' : 'play'}
                        onclick={(e) => {
                            e.stopPropagation()
                            if (active_workflow) {
                                void reset_filters_sort(vnode.attrs.collection)
                            } else if (workflows && workflows.length > 0) {
                                const first_with_count = workflows.find((wf) => wf.total_count > 0)
                                vnode.attrs.filter.selection = first_with_count?.workflow_id ?? ''
                            }
                        }}
                        tip={active_workflow ? $t('filters.actions.deactivate_filter') : $t('filters.workflow.tip')}
                        type='info'
                        variant='toggle'
                    />
                    <div className='filter-workflow-badge-content'>
                        <div className='filter-workflow-badge-label'>{$t('filters.workflow.title')}</div>
                        {active_workflow && active_workflow_def && (
                            <div className='filter-workflow-badge-active-name'>{active_workflow_def.workflow_id}</div>
                        )}
                        {!active_workflow && <div className='filter-workflow-badge-hint'>{$t('filters.workflow.hint')}</div>}
                    </div>
                </div>

                <div className='filter-workflow-options'>
                    {present(workflows, 'workflow filter context').map((workflow) => {
                        const count = workflow.total_count
                        const completed = count === 0
                        const is_active = active_workflow === workflow.workflow_id
                        return (
                            <div
                                className={classes('filter-workflow-option', {
                                    'filter-workflow-option-active': is_active,
                                    'filter-workflow-option-completed': completed,
                                })}
                                key={workflow.workflow_id}
                                onclick={() => {
                                    if (!vnode.attrs.disabled) {
                                        vnode.attrs.filter.selection = workflow.workflow_id
                                    }
                                }}
                                role='radio'
                                aria-checked={is_active}
                                tabIndex={0}
                                onkeydown={(e: KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        if (!vnode.attrs.disabled) {
                                            vnode.attrs.filter.selection = workflow.workflow_id
                                        }
                                    }
                                }}
                            >
                                <div className='filter-workflow-option-radio'>
                                    {completed ? (
                                        <Icon name='checked' type='success' size='s' className='filter-workflow-option-check' />
                                    ) : (
                                        <div className='filter-workflow-option-radio-dot' />
                                    )}
                                </div>
                                <span className='filter-workflow-option-label'>{workflow.workflow_id}</span>
                                <div className='filter-workflow-option-count'>{count}</div>
                            </div>
                        )
                    })}
                </div>

                {active_workflow_def && (
                    <div className='filter-workflow-actions'>
                        <div className='filter-workflow-actions-header'>
                            <div key={`workflow-label-${String(active_workflow)}`} className='filter-workflow-actions-label'>
                                {$t('filters.workflow.suggested_actions')}
                            </div>
                            <Icon
                                key={`workflow-info-${String(active_workflow)}`}
                                name='info'
                                size='s'
                                className='filter-workflow-actions-info'
                                tip={() => this.format_filter_conditions(active_workflow_def)}
                                tip_attrs={{delay: [300, 100], interactive: true, placement: 'right'}}
                            />
                        </div>
                        {active_workflow_def.actions.length > 0 && (
                            <div className='filter-workflow-actions-list'>
                                {active_workflow_def.actions.map((action, index: number) => (
                                    <div key={index} className='filter-workflow-actions-item'>
                                        <Icon name='chevronRight' className='filter-workflow-actions-arrow' />
                                        <span>{action.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {show_include_snoozed && include_snoozed_filter && (
                            <FieldSwitch
                                className='snoozed-switch'
                                help={$t('filters.workflow.include_snoozed_help')}
                                label={$t('filters.workflow.include_snoozed')}
                                model={include_snoozed_filter.$selection}
                                props={{on: true, off: false}}
                                variant='inline'
                            />
                        )}
                    </div>
                )}
            </div>
        )
    }
}
